import { CallLink } from "@/components/calling/CallLink";
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { CalendarPlus, MapPin, Phone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { VISIT_CHANGED, useVisitSettings } from "@/lib/visits";
import { CheckInDialog, type VisitCustomer } from "@/components/visits/VisitDialogs";
import type { Task } from "@/types/task";
import { dateLocale, withClock } from "@/lib/dateTime";

const TYPE_VERB: Record<string, string> = { CALL: "Call", EMAIL: "Email", MEETING: "Meeting", FOLLOW_UP: "Follow up", WHATSAPP: "WhatsApp", OTHER: "To do" };

const when = (iso: string) => {
  const d = new Date(iso);
  const days = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime()) / 86400000);
  const time = d.toLocaleTimeString(dateLocale(), withClock({ hour: "numeric", minute: "2-digit" }));
  if (days === 0) return `today ${time}`;
  if (days === 1) return `tomorrow ${time}`;
  if (days === -1) return `yesterday ${time}`;
  return `${d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })} ${time}`;
};

/**
 * The deal's one next step: its earliest open task. Call, finish or push it back right here, or add one
 * when nothing is planned (a deal with no next step is how deals go quiet).
 */
export function DealNextStep({
  dealId,
  isClosed,
  phone,
  version,
  visitCustomer,
  onAdd,
  onChanged,
}: {
  dealId: string;
  isClosed: boolean;
  /** The primary contact's number, for "Call now" */
  phone?: string | null;
  /** Changes whenever a task is added elsewhere on the page, so this refreshes */
  version: number;
  /** The deal's account, for checking in at an in-person meeting */
  visitCustomer?: VisitCustomer | null;
  onAdd: () => void;
  onChanged: () => void;
}) {
  const { success, error } = useToast();
  const [task, setTask] = useState<Task | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [checkIn, setCheckIn] = useState(false);
  const { hasPermission } = useAuth();
  const visits = useVisitSettings(hasPermission("visit.create"));

  const load = useCallback(async () => {
    try {
      const rows = unwrapPage<Task>(await api.get(`/api/Task/List?related_type=DEAL&related_id=${dealId}&scope=all&page_size=100`)).rows;
      const open = rows.filter((t) => t.task_status === "OPEN").sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
      setTask(open[0] || null);
    } catch {
      setTask(null);
    }
  }, [dealId]);

  useEffect(() => {
    load();
  }, [load, version]);
  // Checking out of a visit for this meeting finishes it
  useEffect(() => {
    const on = () => {
      void load();
      onChanged();
    };
    window.addEventListener(VISIT_CHANGED, on);
    return () => window.removeEventListener(VISIT_CHANGED, on);
  }, [load]);

  if (isClosed || task === undefined) return null;

  const act = async (what: "done" | "later") => {
    if (!task) return;
    setBusy(true);
    try {
      if (what === "done") {
        await api.patch(`/api/Task/Complete/${task.id}`, { outcome: "Done" });
        success("Done", task.title);
      } else {
        const until = new Date();
        until.setDate(until.getDate() + 1);
        until.setHours(9, 0, 0, 0);
        await api.patch(`/api/Task/Snooze/${task.id}`, { until: until.toISOString() });
        success("Moved to tomorrow 9:00", task.title);
      }
      await load();
      onChanged();
    } catch (err: any) {
      error("Could not update the task", err.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  if (!task) {
    return (
      <section aria-label="Next step" className="flex flex-wrap items-center gap-3.5 rounded-xl border border-dashed border-warning bg-warning-soft px-4 py-3.5">
        <span className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-card text-warning">
          <CalendarPlus className="size-[18px]" />
        </span>
        <div className="min-w-[240px] flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-warning">No next step</div>
          <div className="text-[15px] font-semibold text-foreground">Nothing is planned for this deal</div>
          <div className="text-xs text-muted-foreground">Deals with a next step move; the rest go quiet.</div>
        </div>
        <button type="button" onClick={onAdd} className="h-[34px] rounded-lg bg-sidebar px-3.5 text-[13px] font-semibold text-white hover:bg-sidebar/90">
          Schedule a next step
        </button>
      </section>
    );
  }

  const overdue = new Date(task.due_at).getTime() < Date.now();
  // A meeting at the customer's place is met in person: check in there instead of ticking it off
  const inPerson = task.task_type === "MEETING" && (/IN_PERSON|CLIENT_SITE/.test(task.meeting_provider || "") || /Mode: (IN_PERSON|CLIENT_SITE)/.test(task.description || ""));
  const canCheckIn = inPerson && !!visitCustomer && !!visits?.enabled;
  return (
    <section aria-label="Next step" className="flex flex-wrap items-center gap-3.5 rounded-xl bg-sidebar px-4 py-3.5 text-white">
      <span className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-white/10">
        <Phone className="size-[18px]" />
      </span>
      <div className="min-w-[240px] flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gold">
          Next step · {when(task.due_at)}
          {overdue ? " · overdue" : ""}
        </div>
        <div className="text-[15px] font-semibold">{task.title}</div>
        <div className="text-xs text-white/70">{task.description || TYPE_VERB[task.task_type] || "To do"}</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {task.task_type === "CALL" && phone && (
          <CallLink type="DEAL" id={dealId} number={phone} className="inline-flex h-[34px] items-center rounded-lg bg-gold px-3.5 text-[13px] font-bold text-sidebar hover:opacity-90">
            Call now
          </CallLink>
        )}
        {canCheckIn && (
          <button type="button" onClick={() => setCheckIn(true)} className="inline-flex h-[34px] items-center gap-1.5 rounded-lg bg-gold px-3.5 text-[13px] font-bold text-sidebar hover:opacity-90">
            <MapPin className="size-4" /> Check in
          </button>
        )}
        <button type="button" disabled={busy} onClick={() => act("done")} className="h-[34px] rounded-lg border border-white/30 px-3 text-[13px] font-semibold hover:bg-white/10 disabled:opacity-60">
          Done
        </button>
        <button type="button" disabled={busy} onClick={() => act("later")} className="h-[34px] rounded-lg border border-white/30 px-3 text-[13px] font-semibold hover:bg-white/10 disabled:opacity-60">
          Later
        </button>
      </div>
      {canCheckIn && <CheckInDialog open={checkIn} onOpenChange={setCheckIn} customer={{ ...visitCustomer!, taskId: task.id }} />}
    </section>
  );
}
